import { computed, ref } from 'vue';

export default function useNotionDatabase(notionClient) {
  let workspaceName = '';
  const databases = ref({});
  const pages = ref({});
  const loading = ref(false);

  const getTitle = (node) => {
    if (node.title[0] === undefined) {
      return 'Untitled';
    }
    return node.title[0].plain_text;
  };

  const extractParent = async (database) => {
    try {
      const parent = database.parent;
      if (parent.type === 'page_id') {
        let response = pages.value[parent.page_id];
        if (response === undefined) {
          response = await notionClient.client.value.pages.retrieve({ page_id: parent.page_id });
        }

        const name = Object.values(response.properties).reduce((res, next) => {
          if (next.type === 'title') {
            return getTitle(next);
          }
          return res;
        }, '');

        return {
          ...database,
          parentName: name,
          isGrandParentWorkspace: response.parent.type === 'workspace',
        };
      }

      if (parent.type === 'database_id') {
        let response = databases.value[parent.database_id];
        if (response === undefined) {
          response = await notionClient.client.value.databases.retrieve({ database_id: parent.database_id });
          response = { parent: response.parent, name: getTitle(response) };
        }
        return {
          ...database,
          parentName: response.name,
          isGrandParentWorkspace: response.parent.type === 'workspace',
        };
      }

      if (parent.type === 'workspace') {
        return { ...database, parentName: workspaceName, isGrandParentWorkspace: false };
      }

      return { ...database, parentName: '', isGrandParentWorkspace: false };
    } catch (error) {
      return { ...database, parentName: '', isGrandParentWorkspace: false };
    }
  };

  // Takes a data source. Under Notion 2025-09-03 the properties live on the
  // data source rather than the database, and a data source's `parent` is
  // always the database containing it - the page or workspace the UI wants for
  // breadcrumbs moved to `database_parent`. Using `parent` here would show
  // every database as its own parent.
  const formatDatabase = (original) => {
    const containedBy = original.database_parent || original.parent;
    return {
      archived: original.archived,
      // Still the database id, so template.database.id keeps the meaning it has
      // always had and clients running older code keep working.
      id: original.parent.database_id,
      // Where pages are actually created now.
      dataSourceId: original.id,
      name: getTitle(original),
      properties: original.properties,
      parentType: containedBy.type,
      parent: containedBy,
      icon: original.icon,
    };
  };

  const init = async (workspace) => {
    loading.value = true;
    databases.value = {};
    workspaceName = workspace.name;

    const responseDatabases = await notionClient.client.value.search({
      filter: {
        value: 'data_source',
        property: 'object',
      },
    }); // TODO: next_cursor
    const formatted = responseDatabases.results.map(formatDatabase);
    for (let i = 0; i < formatted.length; i++) {
      databases.value[formatted[i].id] = formatted[i];
    }
    const responsePages = await notionClient.client.value.search({ filter: { value: 'page', property: 'object' } }); // TODO: next_cursor

    for (let i = 0; i < responsePages.results.length; i++) {
      const page = responsePages.results[i];
      pages.value[page.id] = page;
    }

    for (let i = 0; i < formatted.length; i++) {
      const databaseId = formatted[i].id;
      // eslint-disable-next-line no-await-in-loop
      databases.value[databaseId] = await extractParent(databases.value[databaseId]);
    }

    loading.value = false;
  };

  const searchByNameLoading = ref(false);
  const searchByName = async (name) => {
    searchByNameLoading.value = true;
    const response = await notionClient.client.value.search({
      query: name,
      filter: { value: 'data_source', property: 'object' },
    }); // TODO: next_cursor

    const result = {};
    for (let i = 0; i < response.results.length; i++) {
      const database = formatDatabase(response.results[i]);
      // eslint-disable-next-line no-await-in-loop
      result[database.id] = await extractParent(database);
    }

    searchByNameLoading.value = false;
    return Object.values(result);
  };

  // Callers hold a database id; the schema now lives on its data source, so
  // this is two hops. Only ever one data source is used - the first - which
  // matches the single-target model the app has always had.
  const retrieveDataSource = async (databaseId) => {
    const database = await notionClient.client.value.databases.retrieve({ database_id: databaseId });
    const first = (database.data_sources || [])[0];
    if (first === undefined) {
      return undefined;
    }
    return notionClient.client.value.dataSources.retrieve({ data_source_id: first.id });
  };

  const getSingle = async (id) => {
    if (databases.value[id] !== undefined) {
      return databases.value[id];
    }
    try {
      const response = await retrieveDataSource(id);
      if (response === undefined) {
        return undefined;
      }
      return await extractParent(formatDatabase(response));
    } catch (error) {
      return undefined;
    }
  };

  const getSingleNoParent = async (id) => {
    if (databases.value[id] !== undefined) {
      return databases.value[id];
    }
    try {
      const response = await retrieveDataSource(id);
      return response === undefined ? undefined : formatDatabase(response);
    } catch (error) {
      return undefined;
    }
  };

  // `id` is a data source id now, not a database id: pages are created under a
  // data source under 2025-09-03.
  const savePage = async (id, properties, content = [], icon = null, cover = null) => notionClient.client.value.pages.create({
    parent: { type: 'data_source_id', data_source_id: id },
    properties,
    children: content,
    icon,
    cover,
  });

  return {
    databases: computed(() => Object.values(databases.value)),
    init,
    getSingle,
    getSingleNoParent,
    savePage,
    searchByName,
    searchByNameLoading,
    loading,
  };
}
